import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Mail, Plus, UserPlus } from 'lucide-react';
import { PERMISSIONS, PERMISSION_KEYS, ROLE_LABELS, ROLES, roleHasPermission, type Permission, type Role } from '@bp/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { AdminUser, Store } from '@/lib/types';
import { fmtDateTime } from '@/lib/utils';
import { Badge, Button, Card, Dialog, ErrorNote, Field, Input, Loading, PageHeader, Select, Tabs } from '@/components/ui';

function StorePicker({ stores, value, onChange }: { stores: Store[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="grid max-h-56 gap-1.5 overflow-y-auto rounded-lg border p-3 text-sm sm:grid-cols-2">
      {stores.length === 0 && <p className="text-muted-fg">No stores yet.</p>}
      {stores.map((s) => (
        <label key={s.id} className="inline-flex items-center gap-1.5">
          <input type="checkbox" className="size-4" checked={value.includes(s.id)} onChange={(e) => onChange(e.target.checked ? [...value, s.id] : value.filter((x) => x !== s.id))} />
          {s.name} <span className="text-muted-fg">#{s.store_number}</span>
        </label>
      ))}
    </div>
  );
}

function InviteLink({ link }: { link: string }) {
  return (
    <div className="rounded-lg bg-muted p-3 text-sm">
      <p className="mb-2">Invite sent. If email isn't set up yet, share this link directly (valid 7 days):</p>
      <div className="flex gap-2"><Input readOnly value={link} onFocus={(e) => e.target.select()} /><Button variant="outline" onClick={() => { void navigator.clipboard.writeText(link); toast.success('Copied'); }}><Copy className="size-4" /></Button></div>
    </div>
  );
}

export function UsersPage() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<AdminUser[]>('/api/users') });
  const stores = useQuery({ queryKey: ['stores', 'all'], queryFn: () => api.get<Store[]>('/api/stores?include_inactive=1') });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const storeName = (id: string) => stores.data?.find((s) => s.id === id)?.name ?? '…';

  if (users.isLoading || stores.isLoading) return <Loading />;
  if (users.error) return <ErrorNote error={users.error} />;

  return (
    <>
      <PageHeader title="Users" subtitle="Invite people, set their role and which stores they can see." actions={<Button onClick={() => setInviting(true)}><UserPlus className="size-4" />Invite</Button>} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-fg uppercase"><tr className="border-b"><th className="px-4 py-2 font-medium">Name</th><th className="px-4 py-2 font-medium">Role</th><th className="px-4 py-2 font-medium">Stores</th><th className="px-4 py-2 font-medium">Last sign-in</th><th /></tr></thead>
          <tbody>
            {users.data!.map((u) => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-muted/50">
                <td className="px-4 py-2.5">
                  <button className="text-left font-medium hover:underline" onClick={() => setEditing(u)}>{u.name}</button>
                  <p className="text-xs text-muted-fg">{u.email}</p>
                </td>
                <td className="px-4 py-2.5">{ROLE_LABELS[u.role]}{Object.keys(u.permission_overrides).length > 0 && <Badge className="ml-2">custom</Badge>}</td>
                <td className="px-4 py-2.5">{u.role === 'admin' ? <span className="text-muted-fg">All stores</span> : u.store_ids.length === 0 ? <Badge tone="warn">None</Badge> : u.store_ids.map(storeName).join(', ')}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">{u.last_login_at ? fmtDateTime(u.last_login_at) : '—'}</td>
                <td className="px-4 py-2.5 text-right">
                  {!u.active && <Badge tone="fail">Deactivated</Badge>}
                  {u.invite_pending && <Badge tone="warn">Invite pending</Badge>}
                  {!u.has_password && !u.invite_pending && u.active && <Badge tone="warn">Invite expired</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {inviting && <InviteDialog stores={stores.data ?? []} onClose={() => setInviting(false)} onDone={() => qc.invalidateQueries({ queryKey: ['users'] })} />}
      {editing && <EditUserDialog user={editing} self={editing.id === me?.id} stores={stores.data ?? []} onClose={() => setEditing(null)} onDone={() => qc.invalidateQueries({ queryKey: ['users'] })} />}
    </>
  );
}

function InviteDialog({ stores, onClose, onDone }: { stores: Store[]; onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('manager');
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Invite someone" footer={link ? <Button onClick={onClose}>Done</Button> : <><Button variant="outline" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={async () => {
      setBusy(true);
      setError(null);
      try {
        const r = await api.post<{ invite_link: string }>('/api/users/invite', { email, name, role, store_ids: storeIds });
        setLink(r.invite_link);
        onDone();
      } catch (e) { setError(e); } finally { setBusy(false); }
    }}><Plus className="size-4" />Send invite</Button></>}>
      {link ? <InviteLink link={link} /> : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          </div>
          <Field label="Role" hint={role === 'store' ? 'A shared login for a store tablet: runs practice audits and works action items.' : undefined}>
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select>
          </Field>
          {role !== 'admin' && <Field label="Stores"><StorePicker stores={stores} value={storeIds} onChange={setStoreIds} /></Field>}
          <ErrorNote error={error} />
        </div>
      )}
    </Dialog>
  );
}

function EditUserDialog({ user, self, stores, onClose, onDone }: { user: AdminUser; self: boolean; stores: Store[]; onClose: () => void; onDone: () => void }) {
  const [tab, setTab] = useState<'access' | 'permissions'>('access');
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<Role>(user.role);
  const [active, setActive] = useState(user.active);
  const [storeIds, setStoreIds] = useState(user.store_ids);
  const [overrides, setOverrides] = useState<Record<string, boolean>>(user.permission_overrides);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const groups = [...new Set(PERMISSION_KEYS.map((k) => PERMISSIONS[k].group))];

  return (
    <Dialog wide open onOpenChange={(o) => !o && onClose()} title={user.name} description={user.email} footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={async () => {
      setBusy(true);
      setError(null);
      try {
        await api.patch(`/api/users/${user.id}`, { name, role, active, store_ids: storeIds, permission_overrides: overrides });
        toast.success('Saved.');
        onDone();
        onClose();
      } catch (e) { setError(e); } finally { setBusy(false); }
    }}>Save</Button></>}>
      <Tabs className="mb-4 w-fit" value={tab} onChange={setTab} tabs={[{ value: 'access', label: 'Access' }, { value: 'permissions', label: 'Permissions' }]} />
      {tab === 'access' ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Role"><Select value={role} disabled={self} onChange={(e) => setRole(e.target.value as Role)}>{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select></Field>
          </div>
          {role !== 'admin' && <Field label="Stores"><StorePicker stores={stores} value={storeIds} onChange={setStoreIds} /></Field>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="size-4" disabled={self} checked={active} onChange={(e) => setActive(e.target.checked)} />Active (unticking signs them out everywhere)</label>
          {!user.has_password && (
            link ? <InviteLink link={link} /> : (
              <Button variant="outline" onClick={async () => { try { const r = await api.post<{ invite_link: string }>(`/api/users/${user.id}/resend-invite`); setLink(r.invite_link); } catch (e) { setError(e); } }}><Mail className="size-4" />Resend invite</Button>
            )
          )}
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <p className="text-muted-fg">Defaults come from the role. Override a permission only when this person needs something different.</p>
          {groups.map((g) => (
            <div key={g}>
              <p className="mb-1 font-semibold">{g}</p>
              <ul className="divide-y rounded-lg border">
                {PERMISSION_KEYS.filter((k) => PERMISSIONS[k].group === g).map((k: Permission) => {
                  const def = roleHasPermission(role, k);
                  const val = k in overrides ? (overrides[k] ? 'grant' : 'deny') : 'default';
                  return (
                    <li key={k} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span>{PERMISSIONS[k].label}</span>
                      <Select className="h-8 w-44" value={val} onChange={(e) => {
                        const v = e.target.value;
                        setOverrides((o) => { const n = { ...o }; if (v === 'default') delete n[k]; else n[k] = v === 'grant'; return n; });
                      }}>
                        <option value="default">Role default ({def ? 'yes' : 'no'})</option>
                        <option value="grant">Always allow</option>
                        <option value="deny">Never allow</option>
                      </Select>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3"><ErrorNote error={error} /></div>
    </Dialog>
  );
}
